import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const scrollPositions = useRef({});
  const visitedPages = useRef(new Set());
  const previousPath = useRef(pathname);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Disable browser's native scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    // On initial mount, scroll to top
    if (isInitialMount.current) {
      window.scrollTo(0, 0);
      visitedPages.current.add(pathname);
      previousPath.current = pathname;
      isInitialMount.current = false;
      return;
    }

    // Check if clicking the same page link (same pathname)
    if (previousPath.current === pathname) {
      // Same page clicked - scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      return;
    }

    // Save scroll position of previous page before navigating away
    scrollPositions.current[previousPath.current] = window.pageYOffset;

    // Check if this page was visited before in this session
    const wasVisited = visitedPages.current.has(pathname);

    if (wasVisited && scrollPositions.current[pathname] !== undefined) {
      // Page was visited before - restore scroll position
      setTimeout(() => {
        window.scrollTo({
          top: scrollPositions.current[pathname],
          behavior: 'instant',
        });
      }, 0);
    } else {
      // First visit to this page - scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    }

    // Mark this page as visited
    visitedPages.current.add(pathname);
    previousPath.current = pathname;
  }, [pathname]);

  return null;
};

export default ScrollToTop;